import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import '../styles/pages/payment.css';
import Mascot from '../components/Mascot';

/**
 * PaymentSuccess.js
 *
 * Handles both Stripe and Razorpay post-payment redirects.
 *
 * Stripe redirects here with:  ?session_id=cs_xxx&plan=individual
 * Razorpay navigates here with: ?gateway=razorpay&plan=individual&razorpay_payment_id=pay_xxx
 *
 * This page:
 *  1. Calls refreshUser() so the React app reflects the new plan immediately.
 *  2. Shows a success state with a countdown back to the dashboard.
 *  3. Sends a postMessage so any open extension content scripts know to refresh.
 */
function PaymentSuccess() {
  const [params]                  = useSearchParams();
  const { refreshUser }           = useAuth();
  const navigate                  = useNavigate();
  const [countdown, setCountdown] = useState(5);
  const [refreshed, setRefreshed] = useState(false);

  const plan      = params.get('plan')    || 'individual';
  const gateway   = params.get('gateway') || 'stripe';
  const planLabel = plan === 'teams' ? 'Teams' : 'Individual';

  // ── Refresh user context + notify extension ─────────────────────────────────
  useEffect(() => {
    refreshUser()
      .then(() => setRefreshed(true))
      .catch(() => setRefreshed(true)); // show success even if refresh fails

    // Notify any open extension content scripts
    window.postMessage({ type: 'MICHII_PAYMENT_SUCCESS', plan }, '*');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Countdown → redirect to my-guides ──────────────────────────────────────
  useEffect(() => {
    if (!refreshed) return;
    if (countdown <= 0) { navigate('/my-guides'); return; }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, refreshed, navigate]);

  return (
    <div className="payment-page">
      <div className="payment-say">
        <Mascot size={40} />
        <div className="payment-say__bubble"><b>Michii says</b><span>Your new features are switched on.</span></div>
      </div>
      <div className="payment-card payment-card--success m-sheet">

        {/* Animated SVG checkmark */}
        <div className="payment-icon-wrap">
          <svg viewBox="0 0 52 52" className="payment-check-svg" aria-hidden="true">
            <circle
              cx="26" cy="26" r="25"
              fill="none" stroke="currentColor" strokeWidth="2"
              className="payment-check-circle"
            />
            <path
              fill="none" stroke="currentColor" strokeWidth="3"
              strokeLinecap="round" strokeLinejoin="round"
              d="M14 27l8 8 16-16"
              className="payment-check-path"
            />
          </svg>
        </div>

        <h1 className="payment-heading">Payment successful</h1>
        <p className="payment-sub">
          Welcome to the <span className="payment-accent">{planLabel}</span> plan.
          {gateway === 'razorpay' ? ' Paid via Razorpay.' : ' Paid via Stripe.'}
        </p>

        <div className="payment-features">
          {plan === 'teams' ? (
            <>
              <Feature text="Unlimited guides for your whole team" />
              <Feature text="AI chat assistant unlocked" />
              <Feature text="PDF export without watermark" />
              <Feature text="Collaborative workspace ready" />
            </>
          ) : (
            <>
              <Feature text="Unlimited guides" />
              <Feature text="AI chat assistant unlocked" />
              <Feature text="PDF export without watermark" />
            </>
          )}
        </div>

        {refreshed ? (
          <p className="payment-redirect">
            Redirecting to My Guides in <span className="payment-accent">{countdown}s</span>…
          </p>
        ) : (
          <p className="payment-redirect">Activating your plan…</p>
        )}

        <button className="lp-btn payment-btn" onClick={() => navigate('/my-guides')}>
          Go to My Guides
        </button>

        {plan === 'teams' && (
          <button className="lp-tlink payment-btn--secondary" onClick={() => navigate('/workspace')}>
            Set up workspace
          </button>
        )}

      </div>
    </div>
  );
}

function Feature({ text }) {
  return (
    <div className="payment-feature-row">
      <span className="payment-feature-check" aria-hidden="true" />
      <span className="payment-feature-text">{text}</span>
    </div>
  );
}

export default PaymentSuccess;